import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { Box, Button, ButtonBase, Card, darken, Dialog, DialogActions, DialogContent, DialogTitle, lighten, Typography, useTheme } from '@mui/material';
import { useKrTheme } from '@/common/theme/KrThemeContext';
import { StringUtils } from '@/common/utils/StringUtils';
import { useState } from 'react';

export const DimRearrangeDialog = ({ codeToDim, activeDims, numFrozenDims = 0, closeDialog }) => {

  const [hiddenDimCodes, setHiddenDimCodes] = useState(() => {
    const allDimCodes = Object.keys(codeToDim);
    const activeDimCodes = activeDims.map(dim => dim.code);
    return allDimCodes.filter(dimCode => !activeDimCodes.includes(dimCode));
  });
  const [activeDimCodes, setActiveDimCodes] = useState(activeDims.map(dim => dim.code));

  const submit = () => {
    closeDialog(activeDimCodes);
  }

  const handleDragEnd = ({ source, destination, draggableId }) => {
    // dropped outside any list
    if (!destination) {
      return;
    }

    // dropped above a frozen dim
    if (destination.droppableId === 'active' && destination.index < numFrozenDims) {
      return;
    }

    // same list reordering
    if (source.droppableId === destination.droppableId) {
      if (source.droppableId === 'hidden') {
        const newHidden = Array.from(hiddenDimCodes);
        newHidden.splice(source.index, 1);
        newHidden.splice(destination.index, 0, draggableId);
        setHiddenDimCodes(newHidden);
      } else {
        const newActive = Array.from(activeDimCodes);
        newActive.splice(source.index, 1);
        newActive.splice(destination.index, 0, draggableId);
        setActiveDimCodes(newActive);
      }
      return;
    }

    // moving between lists
    const sourceList = source.droppableId === 'hidden' ? hiddenDimCodes : activeDimCodes;
    const destList = destination.droppableId === 'hidden' ? hiddenDimCodes : activeDimCodes;
    const setSource = source.droppableId === 'hidden' ? setHiddenDimCodes : setActiveDimCodes;
    const setDest = destination.droppableId === 'hidden' ? setHiddenDimCodes : setActiveDimCodes;

    // remove from source
    const newSource = Array.from(sourceList);
    newSource.splice(source.index, 1);
    setSource(newSource);

    // add to destination
    const newDest = Array.from(destList);
    newDest.splice(destination.index, 0, draggableId);
    setDest(newDest);
  }

  const theme = useTheme();
  const { isDarkMode } = useKrTheme();

  const renderDropColumn = (id, dimCodes) => {

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 0', minWidth: 0, minHeight: 350 }} >
        <Typography align="center" fontWeight="normal" variant="subtitle1" >{`${StringUtils.toUpperCamel(id)} Dimensions`}</Typography>
        <Droppable droppableId={id} >
          {(provided, snapshot) => {
            const { isDraggingOver } = snapshot;

            const bgColor = isDarkMode 
              ? isDraggingOver ? lighten(theme.palette.surface.main, 0.05) : theme.palette.surface.main
              : isDraggingOver ? darken(theme.palette.primary.light, 0.05) : theme.palette.primary.light

            return (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  flex: '1 1 auto',
                  overflowY: 'auto',
                  backgroundColor: bgColor,
                  borderRadius: 1,
                  p: 1,
                  transition: 'background-color 0.2s, opacity 0.2s',
                  '&::-webkit-scrollbar': { display: 'none' },
                }}
              >
                {dimCodes.map((dimCode, index) => {
                  const isFrozen = id === 'active' && index < numFrozenDims;
                  return (
                    <Draggable key={dimCode} draggableId={dimCode} index={index} isDragDisabled={isFrozen} >
                      {(prov, snapshot) => (
                        <ButtonBase
                          // use Card for the outer element so you keep the elevation, border-radius, etc.
                          component={Card}
                          focusRipple           // keeps the ripple centred like a button
                          disableRipple={isFrozen}
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          {...prov.dragHandleProps}
                          sx={{
                            display: 'block',
                            width: '100%',
                            borderRadius: 1,
                            boxShadow: isFrozen ? 0 : 2,
                            mb: 1,
                            p: 1.5,
                            // 2 .  Darker while dragging — snapshot.isDragging comes from react-dnd
                            bgcolor: isFrozen
                              ? theme.palette.action.disabledBackground
                              : snapshot.isDragging
                                ? darken(theme.palette.primary.main, 0.2)
                                : theme.palette.primary.main,
                            cursor: isFrozen ? 'not-allowed' : 'grab',
                            transition: 'background-color 120ms',
                            '&:hover': {
                              bgcolor: isFrozen ? null : darken(theme.palette.primary.main, 0.1),
                            },
                          }}
                        >
                          <Typography>{codeToDim[dimCode].displayName}</Typography>
                        </ButtonBase>
                      )}
                    </Draggable>
                  )
                })}
                {provided.placeholder}
              </Box>
            )
          }}

        </Droppable>
      </Box>
    )
  }

  return (
    <Dialog onClose={() => closeDialog()} open fullWidth maxWidth="md" data-test-id="dim-rearrange-dialog">

      <DialogTitle>Rearrange Dimensions</DialogTitle>

      <DialogContent>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Box
            sx={{
              flex: 1, // takes up remaining vertical space
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              overflowY: 'auto',
              padding: 2,
            }}
          >
            {/* Hidden Dims */}
            {renderDropColumn('hidden', hiddenDimCodes)}

            {/* Active Dims */}
            {renderDropColumn('active', activeDimCodes)}
          </Box>
        </DragDropContext>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => closeDialog()}>Cancel</Button>
        <Button onClick={submit} data-test-id="dim-rearrange-submit-button">Submit</Button>
      </DialogActions>

    </Dialog>
  )
}